/**
 * Game-specific dispute-prep / recording options shown at challenge creation.
 * Keys must match backend/src/utils/verificationPlan.js EVIDENCE_TIPS_BY_GAME.
 */

export const SETTLEMENT_OPTIONS = [
  {
    value: 'api_when_possible',
    title: 'Automatic check (recommended)',
    description:
      'After the match, EWIO can try to read the result from game data (Steam, Riot, Ballchasing, FACEIT, etc.) when both players verify their IDs. You can still confirm the winner manually if that fails.',
  },
  {
    value: 'player_confirm_only',
    title: 'Confirm in the app only',
    description:
      'No automatic game API payout. Both players use “I won” / “I lost” on the challenge page after the match. You can still add IDs for display; they will not trigger auto-payout.',
  },
];

/** @type {Record<number, { id: string, label: string, blurb: string, links: { label: string, href: string }[] }[]>} */
export const EVIDENCE_OPTIONS_BY_GAME = {
  1: [
    {
      id: 'faceit',
      label: 'FACEIT match + demo',
      blurb: 'Play on FACEIT and keep the match link or download the demo from FACEIT if you need proof later.',
      links: [
        { label: 'FACEIT (sign up / download)', href: 'https://www.faceit.com/' },
        { label: 'FACEIT — download CS2 demo', href: 'https://support.faceit.com/hc/en-us/articles/10622392832412-How-to-download-and-watch-a-CS2-demo' },
      ],
    },
    {
      id: 'cs2_steam_demo',
      label: 'Steam match history demo',
      blurb: 'For supported Valve matchmaking matches, download the GOTV demo from your CS2 match history while it is still available.',
      links: [{ label: 'CS2 demo guide (third-party)', href: 'https://csdb.gg/guides/demo-guide' }],
    },
    {
      id: 'leetify',
      label: 'Leetify (stats / demos)',
      blurb: 'Optional third-party analysis if you upload or connect supported demos.',
      links: [{ label: 'Leetify', href: 'https://leetify.com/' }],
    },
    {
      id: 'cs_demo_manager',
      label: 'CS Demo Manager',
      blurb: 'Free tool to inspect demo files and match details on your PC.',
      links: [{ label: 'Download CS Demo Manager', href: 'https://cs-demo-manager.com/' }],
    },
    {
      id: 'obs',
      label: 'OBS Studio (full session recording)',
      blurb: 'Record the whole match window; works for any lobby type.',
      links: [{ label: 'Download OBS Studio', href: 'https://obsproject.com/' }],
    },
    {
      id: 'medal',
      label: 'Medal.tv (clips / recording)',
      blurb: 'Lightweight game capture with optional auto-clipping.',
      links: [{ label: 'Medal.tv', href: 'https://medal.tv/' }],
    },
    {
      id: 'game_bar',
      label: 'Windows Game Bar',
      blurb: 'Built into Windows 10/11: Win + Alt + R to record gameplay.',
      links: [
        {
          label: 'Microsoft — record with Game Bar',
          href: 'https://support.microsoft.com/en-us/windows/use-game-bar-to-record-game-clips-screenshots-and-broadcasts-2f16a6e1-8879-431d-9161-927a75e0efc0',
        },
      ],
    },
  ],
  2: [
    {
      id: 'dota_client_replay',
      label: 'Dota 2 client replay',
      blurb: 'Download the replay from your match history in the Dota client soon after the game (Valve keeps replays for a limited time).',
      links: [{ label: 'Replay download guide', href: 'https://www.hotspawn.com/dota2/guide/how-to-download-dota-2-replays' }],
    },
    {
      id: 'opendota',
      label: 'OpenDota',
      blurb: 'Look up your match by ID for stats and sometimes replay links.',
      links: [{ label: 'OpenDota', href: 'https://www.opendota.com/' }],
    },
    {
      id: 'obs',
      label: 'OBS Studio',
      blurb: 'Full match recording as backup proof.',
      links: [{ label: 'Download OBS Studio', href: 'https://obsproject.com/' }],
    },
    {
      id: 'medal',
      label: 'Medal.tv',
      blurb: 'Optional clips or background recording.',
      links: [{ label: 'Medal.tv', href: 'https://medal.tv/' }],
    },
    {
      id: 'game_bar',
      label: 'Windows Game Bar',
      blurb: 'Win + Alt + R to record without installing extra software.',
      links: [
        {
          label: 'Microsoft — Game Bar',
          href: 'https://support.microsoft.com/en-us/windows/use-game-bar-to-record-game-clips-screenshots-and-broadcasts-2f16a6e1-8879-431d-9161-927a75e0efc0',
        },
      ],
    },
  ],
  3: [
    {
      id: 'lol_rofl',
      label: 'League client replay (.rofl)',
      blurb: 'Download from Match History in the League client. Replays expire when patches change; grab them soon.',
      links: [
        {
          label: 'Riot — Replays FAQ',
          href: 'https://support-leagueoflegends.riotgames.com/hc/en-us/articles/234965248-Replays-FAQ-Pro-Tips',
        },
      ],
    },
    {
      id: 'riot_match_history',
      label: 'Match history screenshot',
      blurb: 'Screenshot the post-game scoreboard in the client as a simple backup (not as strong as replay + video).',
      links: [{ label: 'League of Legends', href: 'https://www.leagueoflegends.com/' }],
    },
    {
      id: 'obs',
      label: 'OBS Studio',
      blurb: 'Best backup for custom games when API history is missing.',
      links: [{ label: 'Download OBS Studio', href: 'https://obsproject.com/' }],
    },
    {
      id: 'medal',
      label: 'Medal.tv',
      blurb: 'Automatic clips or session recording for League.',
      links: [{ label: 'Medal.tv', href: 'https://medal.tv/' }],
    },
    {
      id: 'game_bar',
      label: 'Windows Game Bar',
      blurb: 'Quick built-in recording on Windows.',
      links: [
        {
          label: 'Microsoft — Game Bar',
          href: 'https://support.microsoft.com/en-us/windows/use-game-bar-to-record-game-clips-screenshots-and-broadcasts-2f16a6e1-8879-431d-9161-927a75e0efc0',
        },
      ],
    },
  ],
  4: [
    {
      id: 'ballchasing',
      label: 'Ballchasing.com',
      blurb: 'Upload your Rocket League replay file so there is a public or unlisted record of the match. EWIO’s auto-check uses Ballchasing when configured.',
      links: [
        { label: 'Ballchasing', href: 'https://ballchasing.com/' },
        { label: 'Ballchasing — upload / API token', href: 'https://ballchasing.com/upload' },
        { label: 'Ballchasing FAQ', href: 'https://ballchasing.com/doc/faq' },
      ],
    },
    {
      id: 'bakkesmod_uploader',
      label: 'BakkesMod — Auto Replay Uploader',
      blurb: 'PC plugin that can upload new replays to Ballchasing automatically (requires BakkesMod; online mod availability may change with game updates).',
      links: [
        { label: 'Download BakkesMod', href: 'https://bakkesmod.com/' },
        { label: 'Auto Replay Uploader plugin', href: 'https://bakkesplugins.com/plugin/17' },
      ],
    },
    {
      id: 'obs',
      label: 'OBS Studio',
      blurb: 'Record the full match if you cannot upload a replay.',
      links: [{ label: 'Download OBS Studio', href: 'https://obsproject.com/' }],
    },
    {
      id: 'medal',
      label: 'Medal.tv',
      blurb: 'Popular for Rocket League clips and session capture.',
      links: [{ label: 'Medal.tv', href: 'https://medal.tv/' }],
    },
    {
      id: 'game_bar',
      label: 'Windows Game Bar',
      blurb: 'Built-in recording on Windows.',
      links: [
        {
          label: 'Microsoft — Game Bar',
          href: 'https://support.microsoft.com/en-us/windows/use-game-bar-to-record-game-clips-screenshots-and-broadcasts-2f16a6e1-8879-431d-9161-927a75e0efc0',
        },
      ],
    },
  ],
};
